import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";
import { ArrowUpRight, Building2, Users, MapPin, Factory } from "lucide-react";
import { fetcher } from "@/lib/api";
import { fmtInt, fmtMoney, fmtPct, fmtCompact } from "@/lib/format";
import { KpiBanner, PageTitle } from "@/components/Shared";
import { PersonAvatar } from "@/components/PersonAvatar";
import { SemaforoDot, SemaforoBadge } from "@/components/Semaforo";
import { TrendChart } from "@/components/TrendChart";
import { useViewMode } from "@/components/ViewModeContext";

const RED = "#E2333F";

function DivisionCard({ div, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`division-card-${div.id}`}
      className="group text-left bg-white border border-slate-200 rounded-sm shadow-sm p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-11 h-11 rounded-sm bg-[#E2333F]/10 flex items-center justify-center">
          <Building2 className="w-5 h-5 text-[#E2333F]" />
        </div>
        <SemaforoBadge status={div.status} />
      </div>
      <h3 className="font-display font-bold text-xl text-slate-900 tracking-tight">
        {div.name}
      </h3>
      <div className="flex items-center gap-2 mt-1 mb-4">
        <PersonAvatar name={div.director} size="xs" />
        <p className="text-sm text-slate-500">Dir. {div.director}</p>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <Stat icon={MapPin} value={div.num_zonas} label="Zonas" />
        <Stat icon={Users} value={div.num_regionales} label="Regionales" />
        <Stat icon={Factory} value={div.num_empresas} label="Empresas" />
      </div>
      <div className="border-t border-slate-100 pt-3 flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
            Tendencia ventas
          </div>
          <div className="font-display font-black text-lg text-slate-900">
            {fmtInt(div.tendencia_kilos)} <span className="text-xs text-slate-400">kg</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
            Util. $/kg
          </div>
          <div className="font-display font-black text-lg text-[#E2333F]">
            {fmtMoney(div.utilidad_por_kilo)}
          </div>
        </div>
      </div>
    </button>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="flex flex-col items-center bg-slate-50 rounded-sm py-2">
      <Icon className="w-4 h-4 text-slate-400 mb-0.5" />
      <span className="font-display font-bold text-base text-slate-900 leading-none">
        {value}
      </span>
      <span className="text-[10px] uppercase tracking-wide text-slate-400 mt-0.5">
        {label}
      </span>
    </div>
  );
}

export default function Overview() {
  const navigate = useNavigate();
  const { showData } = useViewMode();
  const { data, isLoading } = useQuery({
    queryKey: ["overview"],
    queryFn: () => fetcher("/overview"),
  });

  if (isLoading || !data) {
    return <div className="text-slate-400 text-sm">Cargando indicadores…</div>;
  }

  const { totals, divisions, group } = data;
  const barData = divisions.map((d) => ({
    mes: d.name.replace("División ", ""),
    valor: d.tendencia_kilos,
    id: d.id,
  }));

  return (
    <div className="zae-fade max-w-[1400px] mx-auto">
      <PageTitle
        eyebrow="Resumen ejecutivo · Grupo Tomza"
        title={group.name}
        subtitle={`Estructura organizacional · Periodo ${group.period} · ${totals.num_empresas} empresas operativas`}
      />

      <KpiBanner
        stats={[
          { label: "Tendencia ventas", value: `${fmtInt(totals.tendencia_kilos)}`, sub: "kilos" },
          { label: "Acumulado mes", value: `${fmtInt(totals.acumulado)}`, sub: "kilos" },
          { label: "Utilidad gestión", value: fmtMoney(totals.utilidad_por_kilo), sub: "promedio $/kilo" },
          { label: "HG Neto", value: fmtPct(totals.hg_neto_pct), sub: "promedio inventario" },
          { label: "Unidades en taller", value: fmtInt(totals.unidades_taller), sub: "flota total" },
          { label: "Empresas", value: fmtInt(totals.num_empresas), sub: `${divisions.length} divisiones` },
        ]}
      />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5">
          <div className="flex items-center justify-between mb-1">
            <span className="bg-[#E2333F] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-sm">
              Tendencia de ventas · Grupo
            </span>
            <span className="text-xs text-slate-400">kilos · Jul–Jun</span>
          </div>
          <TrendChart
            data={totals.ventas_trend}
            height={240}
            formatter={fmtCompact}
            testId="group-ventas-trend"
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="bg-[#E2333F] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-sm">
              Ventas por división
            </span>
            <span className="text-xs text-slate-400">click para detalle</span>
          </div>
          <div style={{ height: 240 }}>
            {showData ? (
              <div className="h-full flex flex-col justify-center gap-2" data-testid="division-bar-data">
                {barData.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => navigate(`/division/${e.id}`)}
                    className="flex items-center justify-between px-3 py-2.5 rounded-sm bg-slate-50 border border-slate-100 hover:border-[#E2333F]/30 hover:bg-[#E2333F]/5 transition-colors text-left"
                  >
                    <span className="text-sm font-semibold text-slate-700">{e.mes}</span>
                    <span className="font-display font-black text-lg text-slate-900 tabular-nums">
                      {fmtInt(e.valor)} <span className="text-xs text-slate-400 font-medium">kg</span>
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E9ECF1" vertical={false} />
                  <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={48} tickFormatter={fmtCompact} />
                  <Tooltip
                    cursor={{ fill: "#F1F5F9" }}
                    formatter={(v) => [`${fmtInt(v)} kg`, "Tendencia"]}
                    contentStyle={{ borderRadius: 4, border: "1px solid #E2E8F0", fontSize: 12 }}
                  />
                  <Bar
                    dataKey="valor"
                    radius={[3, 3, 0, 0]}
                    cursor="pointer"
                    onClick={(d) => navigate(`/division/${d.id}`)}
                  >
                    {barData.map((e, i) => (
                      <Cell key={i} fill={RED} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="mb-3 flex items-center gap-2">
        <h2 className="font-display font-bold text-lg text-slate-800">Divisiones</h2>
        <span className="text-sm text-slate-400">— selecciona para explorar zonas, regionales y empresas</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {divisions.map((div) => (
          <DivisionCard key={div.id} div={div} onClick={() => navigate(`/division/${div.id}`)} />
        ))}
      </div>
    </div>
  );
}
